/* eslint-disable max-lines */
import Button from '@components/Button/Button'
import CloudUserDataRecovery from '@components/Dashboard/CloudUserDataRecovery'
import DailyReadingTime from '@components/Dashboard/DailyReadingTime'
import HomeButtonsConfig from '@components/Dashboard/HomeButtonsConfig/HomeButtonsConfig'
import Notifications from '@components/Dashboard/Notifications'
import StatsChart from '@components/Dashboard/StatsChart/StatsChart'
import UserHistory from '@components/Dashboard/UserHistory'
import StatsRecovery from '@components/Home/StatsRecovery'
import LoadingSpinner from '@components/shared/Loading/Loading'
import { useAuth } from '@contexts/AuthContext'
import { useUserData } from '@contexts/UserDataContext'
import { useIsNativeApp } from '@hooks/useIsNativeApp'
import { getNameByPageType } from 'helpers/userData'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { FaArrowRight } from 'react-icons/fa'

// Add these icons (using actual icon components from your project)
const SavedIcon = () => <span className="mr-1">📑</span>
const NotificationIcon = () => <span className="mr-1">🔔</span>
const HomeIcon = () => <span className="mr-1">🏠</span>
const StatsIcon = () => <span className="mr-1">📊</span>
const HistoryIcon = () => <span className="mr-1">⏱️</span>
const RecoveryIcon = () => <span className="mr-1">🛟</span>
const SettingsIcon = () => <span className="mr-1">⚙️</span>

const MyAccount = () => {
  const { userData, updateUserData } = useUserData()
  const [isLoading, setIsLoading] = useState(false)
  const { user, resetPassword, deleteAccount, loading } = useAuth()
  const [activeTab, setActiveTab] = useState('saved')
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const router = useRouter()
  const isNativeApp = useIsNativeApp()

  const savedPages = userData.savedPages

  // Handle tab selection from URL query parameter
  useEffect(() => {
    const { tab } = router.query
    const validTabs = [
      'saved',
      ...(isNativeApp ? [] : ['notifications']),
      'homeConfig',
      'stats',
      'history',
      'recovery',
      'settings',
    ]

    if (tab && typeof tab === 'string' && validTabs.includes(tab)) {
      setActiveTab(tab)
    }
  }, [router.query, isNativeApp])

  useEffect(() => {
    if (isNativeApp && activeTab === 'notifications') {
      setActiveTab('saved')
    }
  }, [activeTab, isNativeApp])

  // Update URL when tab changes
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId)
    router.push(
      {
        pathname: '/hesabim',
        query: { tab: tabId },
      },
      undefined,
      { shallow: true },
    )
  }

  const handlePasswordReset = async () => {
    try {
      if (window.confirm('Şifrenizi sıfırlamak istediğinize emin misiniz?')) {
        setIsLoading(true)
        await resetPassword(user.email)
        toast.success('Şifre sıfırlama linki e-posta adresinize gönderildi.', {
          duration: 3000,
        })
      }
    } catch {
      toast.error('Şifre sıfırlama maili gönderilemedi. Lütfen tekrar deneyin.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!user?.uid) {
      toast.error('Hesap silme işlemi için giriş yapmanız gerekiyor.')
      return
    }

    if (
      !window.confirm(
        'Hesabınızı kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
      )
    ) {
      return
    }

    try {
      setIsLoading(true)
      await deleteAccount()
    } catch (error: any) {
      if (error?.code === 'auth/requires-recent-login') {
        toast.error(
          'Güvenlik için tekrar giriş yapmanız gerekiyor. Lütfen çıkış yapıp tekrar giriş yapın.',
        )
      } else {
        toast.error('Hesap silinemedi. Lütfen tekrar deneyin.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  const tabs = [
    {
      id: 'saved',
      label: 'Kaydedilen Sayfalar',
      icon: <SavedIcon />,
      count: savedPages.length,
    },
    ...(isNativeApp
      ? []
      : [
          {
            id: 'notifications',
            label: 'Bildirimler',
            icon: <NotificationIcon />,
            count: 0,
          },
        ]),
    { id: 'homeConfig', label: 'Ana Sayfa Ayarları', icon: <HomeIcon /> },
    { id: 'stats', label: 'İstatistikler', icon: <StatsIcon /> },
    { id: 'history', label: 'Geçmiş', icon: <HistoryIcon /> },
    { id: 'recovery', label: 'Veri Kurtarma', icon: <RecoveryIcon /> },
    { id: 'settings', label: 'Ayarlar', icon: <SettingsIcon /> },
  ]

  const clearSavedPages = () => {
    if (window.confirm('Tüm kayıtlı sayfaları silmek istediğinize emin misiniz?')) {
      updateUserData('savedPages', [])
      toast.success('Tüm kayıtlı sayfalar silindi.')
    }
  }

  return (
    <div className="mx-auto py-4 max-w-2xl">
      <h1 className="mb-6 font-bold text-2xl sm:text-left text-center">Hesabım</h1>

      {/* Mobile Menu Button - Only visible on small screens */}
      <div className="sm:hidden mb-4">
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="flex justify-between items-center bg-white dark:bg-gray-800 shadow-xs p-3 border dark:border-gray-700 rounded-lg w-full"
        >
          <span className="flex items-center font-medium">
            {tabs.find((tab) => tab.id === activeTab)?.icon}
            {tabs.find((tab) => tab.id === activeTab)?.label}
          </span>
          <span
            className="transition-transform duration-200 transform"
            style={{ transform: showMobileMenu ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            ▼
          </span>
        </button>

        {showMobileMenu && (
          <div className="right-4 left-4 z-10 absolute bg-white dark:bg-gray-800 shadow-md mt-2 border dark:border-gray-700 rounded-lg overflow-hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  handleTabChange(tab.id)
                  setShowMobileMenu(false)
                }}
                className={`flex w-full items-center justify-between border-b p-3 text-left last:border-b-0 dark:border-gray-700
                  ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                      : ''
                  }
                `}
              >
                <span className="flex items-center">
                  {tab.icon}
                  {tab.label}
                </span>
                {tab.count !== undefined && tab.count > 0 && (
                  <span className="bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-full font-medium text-blue-800 dark:text-blue-300 text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tabs Navigation - Hidden on mobile */}
      <div className="hidden sm:block mb-6 dark:border-gray-600 border-b overflow-x-auto">
        <div className="flex space-x-2 md:space-x-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors md:text-base
                ${
                  activeTab === tab.id
                    ? 'border-b-2 border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className="bg-blue-100 dark:bg-blue-900/40 ml-2 px-2 py-0.5 rounded-full font-medium text-blue-800 dark:text-blue-300 text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 shadow-xs mb-6 border dark:border-gray-700 rounded-lg">
        {activeTab === 'saved' && (
          <div className="p-3">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-bold text-lg">Kaydedilen Sayfalar</h2>
              {savedPages.length > 0 && (
                <Button
                  onClick={clearSavedPages}
                  className="flex items-center px-2 py-1 text-xs"
                  color="red"
                >
                  <span className="mr-1">🗑️</span> Temizle
                </Button>
              )}
            </div>

            {savedPages.length ? (
              <>
                <div className="flex items-center bg-blue-50 dark:bg-blue-900/10 mb-3 px-2 py-1 rounded-sm text-blue-700 dark:text-blue-300 text-xs">
                  <span className="mr-1">📚</span> {savedPages.length} sayfa
                </div>

                <div className="gap-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                  {savedPages.map((savedPage) => (
                    <div
                      key={savedPage.name}
                      className="group relative bg-gray-50 hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:bg-gray-700/50 border dark:border-gray-600 rounded-md overflow-hidden transition-all duration-200"
                    >
                      <div className="top-1 right-1 absolute">
                        <button
                          onClick={() => {
                            const newSavedPages = savedPages.filter(
                              (page) => page.name !== savedPage.name,
                            )
                            updateUserData('savedPages', newSavedPages)
                            toast.success('Sayfa favorilerden kaldırıldı')
                          }}
                          className="bg-white/80 hover:bg-red-100 dark:hover:bg-red-900/30 dark:bg-gray-600/80 p-1 rounded-full hover:text-red-500 text-xs transition-colors"
                          title="Kaldır"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="p-2">
                        <h3 className="mb-1 pr-5 font-medium text-base truncate">
                          {savedPage.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-1 mb-2 text-gray-500 dark:text-gray-400 text-xs">
                          <span className="bg-blue-100 dark:bg-blue-900/40 px-1.5 py-0.5 rounded-xs text-blue-800 dark:text-blue-300">
                            {getNameByPageType(savedPage.type)}
                          </span>
                          <span>Sayfa: {savedPage.page}</span>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            href={getNativeReaderHref(
                              `${savedPage.basePath || ''}/${savedPage.type}/${savedPage.page}`,
                            )}
                            className="flex items-center px-2 py-1 text-xs"
                          >
                            <span>Git</span>
                            <span className="ml-1">
                              <FaArrowRight />
                            </span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-center">
                <div className="inline-flex justify-center items-center bg-blue-100 dark:bg-blue-900/30 mb-3 p-2 rounded-full text-3xl">
                  📑
                </div>
                <h3 className="mb-1 font-medium">Henüz Kaydedilmiş Sayfa Yok</h3>
                <p className="mx-auto mb-2 max-w-md text-gray-600 dark:text-gray-300 text-sm">
                  Favori sayfalarınızı kaydetmek için
                  <span className="bg-gray-200 dark:bg-gray-600 mx-1 px-1 py-0.5 rounded-sm text-xs">
                    Kaydet
                  </span>
                  düğmesini kullanabilirsiniz.
                </p>
                <Button href="/" color="blue" className="mx-auto px-3 py-1 text-xs">
                  Ana Sayfaya Git
                </Button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && !isNativeApp && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg">Bildirimler</h2>
            </div>
            <Notifications />
          </div>
        )}

        {activeTab === 'homeConfig' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg">Ana Sayfa Ayarları</h2>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/10 mb-4 px-3 py-1.5 rounded-md text-yellow-700 dark:text-yellow-300 text-sm">
              Bu ayarlar ana sayfanızın görünümünü özelleştirir
            </div>
            <HomeButtonsConfig />
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg">Detaylı İstatistikler</h2>
            </div>
            <StatsChart />

            {/* Daily Reading Time */}
            <div className="mt-8 pt-6 dark:border-gray-600 border-t">
              <h3 className="mb-4 font-bold text-md">Okuma Süreleri</h3>
              <DailyReadingTime />
            </div>

            <div className="mt-8 pt-6 dark:border-gray-600 border-t">
              <h3 className="mb-4 font-bold text-md">İstatistikleri Sıfırla</h3>
              <div className="flex flex-col space-y-4">
                <Button
                  onClick={() => {
                    if (
                      window.confirm(
                        'İstatistikleri sıfırlamak istediğinize emin misiniz?',
                      )
                    ) {
                      updateUserData('stats', [])
                      toast.success('İstatistikler sıfırlandı.')
                    }
                  }}
                  disabled={isLoading}
                  className="py-2 w-full sm:w-auto"
                  color="red"
                >
                  İstatistikleri Sıfırla
                </Button>
                <p className="text-gray-600 dark:text-gray-400 text-sm italic">
                  İstatistiklerinizi sıfırlamak istediğinizde bu butonu kullanabilirsiniz.
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'recovery' && (
          <div className="space-y-6 p-4">
            <div>
              <h2 className="font-bold text-lg">Veri Kurtarma</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Yerel istatistik yedeklerinizi veya buluttan alınmış son veri
                yedeklerinizi geri yükleyebilirsiniz.
              </p>
            </div>
            <StatsRecovery />
            <CloudUserDataRecovery />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-lg">Kullanıcı Geçmişi</h2>
              <Button
                onClick={() => {
                  if (window.confirm('Geçmişi temizlemek istediğinize emin misiniz?')) {
                    updateUserData('stats', [])
                    toast.success('Geçmiş temizlendi.')
                  }
                }}
                className="px-2 py-1 text-xs"
                color="gray"
              >
                Geçmişi Temizle
              </Button>
            </div>
            <p className="flex items-center mb-4 text-gray-600 dark:text-gray-400 text-sm italic">
              <span className="mr-1">🔒</span> Bu bilgi sadece cihazınızda saklanacak.
            </p>

            <UserHistory />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-8 p-4">
            {user?.email && (
              <div className="bg-linear-to-r from-blue-50 dark:from-blue-900/20 to-purple-50 dark:to-purple-900/20 p-6 rounded-lg">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex justify-center items-center bg-gray-200 dark:bg-gray-600 rounded-full w-16 h-16 text-2xl">
                    {user.email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{user.email}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Kullanıcı hesabı
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h2 className="mb-4 font-bold text-lg">Uygulama İpuçları</h2>
              <div className="flex flex-col space-y-4">
                <Button
                  onClick={() => {
                    if (
                      window.confirm(
                        'Site öğreticisini sıfırlamak istediğinize emin misiniz?',
                      )
                    ) {
                      updateUserData('intros', [])
                      toast.success('Site öğreticisi sıfırlandı.')
                    }
                  }}
                  disabled={isLoading}
                  className="py-2 w-full sm:w-auto"
                  color="blue"
                >
                  İpucu Durumunu Sıfırla
                </Button>
                <p className="text-gray-600 dark:text-gray-400 text-sm italic">
                  Üzerine tıkladığınız her ipucu bir daha size görünmeyecektir. İpuçlarını
                  yeniden görmek için bu butonu kullanabilirsiniz.
                </p>
              </div>
            </div>

            {user?.email ? (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h2 className="mb-4 font-bold text-lg">Şifre Değiştir</h2>
                <Button
                  onClick={handlePasswordReset}
                  disabled={isLoading}
                  className="py-2 w-full sm:w-auto"
                  color="blue"
                >
                  {isLoading ? 'Gönderiliyor...' : 'Şifre Değiştirme Emaili Gönder'}
                </Button>
              </div>
            ) : (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg text-center">
                <div className="mb-4 text-5xl">🔐</div>
                <p className="mb-5 font-medium">
                  Burada daha fazla şey görebilmek ve uygulamadan tamamen faydalanabilmek
                  için hemen ücretsiz bir hesap oluşturun.
                </p>
                <Link
                  href="/kayit-ol"
                  className="inline-block bg-blue-500 hover:bg-blue-600 px-6 py-3 rounded-sm text-white transition-colors"
                >
                  Ücretsiz Hesap Oluştur
                </Link>
              </div>
            )}

            {user?.email && (
              <div className="pt-4 dark:border-gray-600 border-t">
                <button
                  onClick={handleDeleteAccount}
                  disabled={isLoading}
                  className="font-medium text-red-500 hover:text-red-700 disabled:opacity-50 text-sm"
                >
                  Hesabımı Sil
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default MyAccount
